import styled, { css } from "styled-components";

/** How the thumbnail is drawn: the shape of the crop the screen uses, always 56px tall. */
export type ImageFieldShape = "round" | "square" | "landscape" | "wide" | "free";

const THUMB_WIDTH: Record<ImageFieldShape, string> = {
  round: "56px",
  square: "56px",
  landscape: "75px", // 4:3
  wide: "107px", // 1200 x 630, the share image
  free: "75px", // any crop: shown whole inside a 4:3 box
};

/**
 * The whole block in one frame: the thumbnail and its texts on top, the actions in a strip below.
 * Inside another card (`$inset`) it takes the editor's background, to still stand apart.
 */
export const Frame = styled.div<{ $inset?: boolean }>`
  overflow: hidden;
  font-family: "Inter", sans-serif;
  background: ${props => (props.$inset ? "var(--color-neutral-darkness)" : "var(--color-neutral-dark)")};
  border-radius: 16px;
`;

export const Row = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px;
`;

export const Thumb = styled.div<{ $shape: ImageFieldShape; $empty?: boolean; $error?: boolean }>`
  position: relative;
  display: grid;
  flex: none;
  place-content: center;
  width: ${props => THUMB_WIDTH[props.$shape]};
  height: 56px;
  overflow: hidden;
  color: var(--color-neutral-light);
  background: var(--color-neutral-darkness);
  border-radius: ${props => (props.$shape === "round" ? "50%" : "10px")};
  transition: box-shadow 0.15s;

  img {
    width: 100%;
    height: 100%;
    object-fit: ${props => (props.$shape === "free" ? "contain" : "cover")};
  }

  ${props =>
    props.$empty &&
    css`
      background: none;
      border: 1.5px dashed var(--color-neutral-base);
    `}

  ${props =>
    props.$error &&
    css`
      box-shadow: 0 0 0 2px var(--color-feedback-danger);
    `}

  /* while the image goes up: a veil with the spinner over what was there */
  .veil {
    position: absolute;
    inset: 0;
    display: grid;
    place-content: center;
    background: rgba(24, 26, 32, 0.6);
  }
`;

/* The thumbnail opens the upload too */
export const ThumbButton = styled.span`
  display: block;
  flex: none;
  cursor: pointer;

  &:hover ${Thumb} {
    box-shadow: 0 0 0 2px var(--color-neutral-light);
  }
`;

export const Texts = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  .title,
  .hint {
    font-family: "Inter", sans-serif;
  }

  .title {
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.125rem;
    color: var(--color-neutral-white);
  }

  .hint {
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }

  .hint.error {
    color: var(--color-feedback-danger);
  }
`;

/** The actions' strip: equal buttons split by a line. */
export const Actions = styled.div`
  display: flex;
  border-top: 1px solid var(--color-neutral-base);

  /* two actions (Trocar, Remover): a line between them, as the one above */
  > * + * {
    border-left: 1px solid var(--color-neutral-base);
  }
`;

export type ActionTone = "default" | "primary" | "danger";

const TONE_COLOR: Record<ActionTone, string> = {
  default: "var(--color-neutral-white)",
  primary: "var(--color-brand-plus)",
  danger: "var(--color-feedback-danger)",
};

export const Action = styled.button<{ $tone?: ActionTone }>`
  display: inline-flex;
  flex: 1;
  gap: 6px;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 12px;
  font-family: "Inter", sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${props => TONE_COLOR[props.$tone || "default"]};
  white-space: nowrap;
  cursor: pointer;
  background: none;
  transition: background-color 0.15s;

  & + & {
    border-left: 1px solid var(--color-neutral-base);
  }

  &:hover:not(:disabled, [aria-disabled="true"]) {
    background: rgba(255, 255, 255, 0.04);
  }

  &:disabled,
  &[aria-disabled="true"] {
    cursor: default;
    opacity: 0.55;
  }

  &:focus-visible {
    outline: 2px solid var(--color-neutral-white);
    outline-offset: -2px;
  }
`;

export const Spinner = styled.span<{ $size?: number }>`
  display: inline-block;
  width: ${props => props.$size ?? 22}px;
  height: ${props => props.$size ?? 22}px;
  border: 2px solid rgba(255, 255, 255, 0.25);
  border-top-color: var(--color-neutral-white);
  border-radius: 50%;
  animation: image-field-spin 0.9s linear infinite;

  @keyframes image-field-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
